import React, { useState, useEffect } from "react";
import { Card, Modal, Button } from "react-bootstrap";
import { Field, Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DefaultButton from "../../../../shared/components/DefaultButton";
import { SAVE } from "../../../../shared/constants/Constants";
import { v4 as uuid } from "uuid";
import { loadQuestionTemplateDataRequest } from "../../questions/redux/Actions";
import { useSelector, useDispatch } from "react-redux";
import CategoryQT from "./CategoryQT";

const CategoryQuestions = ({
  values,
  setFieldValue,
  isEditMode,
  onQuestionModeChange,
  onTemplateSelect
}) => {
  const dispatch = useDispatch();
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all"); // "all", "active", "inactive"
  const [submitType, setSubmitType] = useState("");
  const { questionlist } = useSelector((state) => state.question);

  const isTemplateMode = values.questionMode === "template";

  const selectedTemplate = values.templateId
    ? questionlist?.data?.find((tpl) => tpl.id === Number(values.templateId))
    : null;

  useEffect(() => {
    dispatch(loadQuestionTemplateDataRequest());
  }, [dispatch]);

  const getQuestionIndex = (question) =>
    values.questions.findIndex(
      (q) =>
        (q.id && q.id === question.id) ||
        (q.tempId && q.tempId === question.tempId)
    );

  //  Validation Schema
  const QuestionSchema = Yup.object().shape({
    name: Yup.string().required("Question is required"),
    answerType: Yup.string().required("Answer Type is required"),
    answerValidation: Yup.string(),
    required: Yup.boolean(),
    is_active: Yup.boolean(),
    order: Yup.number()
      .nullable()
      .when("is_active", {
        is: true,
        then: (schema) => schema.required("Order is required").min(1),
        otherwise: (schema) => schema.nullable(),
      }),
  });

  //  Initial Values
  const initialQuestionValues = {
    name: "",
    answerType: "text",
    answerValidation: "",
    required: false,
    is_active: true,
    order: values.questions ? values.questions.length + 1 : 1,
  };

  // Template change handler
  const handleTemplateChange = (e) => {
    const templateId = e.target.value ? Number(e.target.value) : null;
    setFieldValue("templateId", templateId);

    if (templateId && onTemplateSelect) {
      // Only pass minimal info if it's from a template
      onTemplateSelect({
        templateId: Number(templateId),
        questions: [{ isFromTemplate: true, templateId: Number(templateId) }],
      });
    }
  };

  //  Save Question

  const handleSaveQuestion = (formValues, { resetForm }, submitType) => {
    let updatedQuestions = [...(values.questions || [])];

    if (formValues.is_active && !formValues.order) {
      formValues.order = updatedQuestions.filter((q) => q.is_active).length + 1;
    }

    if (editIndex !== null) {
      const oldQuestion = updatedQuestions[editIndex];

      const updatedQuestion = {
        ...oldQuestion,
        ...formValues,
        //  Keep order only if active
        order: formValues.is_active
          ? oldQuestion.order ||
            updatedQuestions.filter((q) => q.is_active).length + 1
          : null,
        ...(isEditMode && { isEdited: true }),
      };

      updatedQuestions[editIndex] = updatedQuestion;
    } else {
      // New question
      updatedQuestions.push({
        tempId: uuid(), // temporary ID for drag & drop
        ...formValues,
        order: formValues.is_active
          ? updatedQuestions.filter((q) => q.is_active).length + 1
          : null,
        ...(isEditMode && { isEdited: false }),
      });
    }

    //  Reassign order for active questions only
    let activeOrder = 1;
    updatedQuestions = updatedQuestions.map((q) => {
      if (q.is_active) {
        return { ...q, order: activeOrder++ };
      }
      return { ...q, order: null };
    });

    //  Save all updated states (including inactive changes)
    setFieldValue("questions", updatedQuestions);
    if (submitType === "save_new") {
      //resetForm();                 // clear fields
      setShowQuestionModal(true);
      return; // keep modal open
    }

    resetForm();
    setShowQuestionModal(false);
    setEditIndex(null);
  };

  //  Delete Question
  const handleDeleteQuestion = (index) => {
    const questionToDelete = values.questions[index];

    // If it’s a new unsaved question (no ID), remove it completely
    if (!questionToDelete.id) {
      const updatedQuestions = values.questions.filter((_, i) => i !== index);

      // Recalculate order for active ones only
      let activeOrder = 1;
      const reOrdered = updatedQuestions.map((q) => {
        if (q.is_active) {
          return { ...q, order: activeOrder++ };
        }
        return { ...q, order: null };
      });

      setFieldValue("questions", reOrdered);
    } else {
      // Existing question — mark inactive
      const updatedQuestions = values.questions.map((q, i) =>
        i === index ? { ...q, is_active: false, order: null } : q
      );

      // Recalculate order for active ones only
      let activeOrder = 1;
      const reOrdered = updatedQuestions.map((q) => {
        if (q.is_active) {
          return { ...q, order: activeOrder++ };
        }
        return q;
      });

      setFieldValue("questions", reOrdered);
    }

    setEditIndex(null);
  };

  //  Edit Question
  const handleEditQuestion = (index) => {
    setEditIndex(index);
    setShowQuestionModal(true);
  };

  //  Handle Drag End
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (activeFilter !== "active") return;

    // Get ACTIVE questions in current order
    const activeQuestions = values.questions.filter((q) => q.is_active);

    const reorderedActive = Array.from(activeQuestions);
    const [moved] = reorderedActive.splice(result.source.index, 1);
    reorderedActive.splice(result.destination.index, 0, moved);

    // Merge back into full list
    let activeIndex = 0;
    const updatedAll = values.questions.map((q) => {
      if (!q.is_active) return q;

      const updated = reorderedActive[activeIndex++];
      return { ...updated };
    });

    // Reassign order ONLY for active questions
    let order = 1;
    const finalQuestions = updatedAll.map((q) =>
      q.is_active
        ? {
            ...q,
            order: order++,
            ...(isEditMode && q.id && { isEdited: true }),
          }
        : { ...q, order: null }
    );
    setFieldValue("questions", finalQuestions);
  };

  // Filter questions based on active status
  const getFilteredQuestions = () => {
    if (!values.questions) return [];

    switch (activeFilter) {
      case "active":
        return values.questions.filter((q) => q.is_active === true);
      case "inactive":
        return values.questions.filter((q) => q.is_active === false);
      default:
        return values.questions;
    }
  };

  const filteredQuestions = getFilteredQuestions();

  return (
    <Card className="mb-4">
      <Card.Header>Category Questions</Card.Header>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
          {/* Toggle Buttons */}
          <div className="btn-group" role="group" aria-label="Question Mode">
            <button
              type="button"
              className={`btn btn-sm ${
                !isTemplateMode ? "question-tabs-selected" : "question-tabs"
              }`}
            onClick={() => onQuestionModeChange("custom")}  
            >
              Custom Question
            </button>

            <button
              type="button"
              className={`btn btn-sm ${
                isTemplateMode ? "question-tabs-selected" : "question-tabs"
              }`}
              onClick={() => onQuestionModeChange("template")}
            >
              Template Based
            </button>
          </div>

          {/* Right-side Controls */}
          {/* Active/Inactive Filter Toggle Buttons */}
          {!isTemplateMode && (
            <div
              className="btn-group"
              role="group"
              aria-label="Question filter"
            >
              <button
                type="button"
                className={`btn btn-sm ${
                  activeFilter === "all" ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => setActiveFilter("all")}
              >
                All
              </button>
              <button
                type="button"
                className={`btn btn-sm ${
                  activeFilter === "active"
                    ? "btn-success"
                    : "btn-outline-success"
                }`}
                onClick={() => setActiveFilter("active")}
              >
                Active
              </button>
              <button
                type="button"
                className={`btn btn-sm ${
                  activeFilter === "inactive"
                    ? "btn-danger"
                    : "btn-outline-danger"
                }`}
                onClick={() => setActiveFilter("inactive")}
              >
                Inactive
              </button>
            </div>
          )}
          {!isTemplateMode && (
            <DefaultButton
              buttonClass="btn btn-secondary default-btn-color"
              buttonTitle="Add Question"
              buttonType="button"
              actionBlock={() => {
                setEditIndex(null);
                setShowQuestionModal(true);
              }}
            />
          )}

          {isTemplateMode && (
            <select
              className="form-select search-input-outline"
              style={{ maxWidth: "200px", height: "34px", boxShadow: "none", fontSize: "14px" }}
              value={values.templateId || ""}
              onChange={handleTemplateChange}
            >
              <option value="">Select template</option>
              {questionlist?.data?.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.template_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* --------- Display Template Questions Read-Only --------- */}
        {isTemplateMode && selectedTemplate && (
          <div className="mt-3">
            <CategoryQT template={selectedTemplate} />
          </div>
        )}
        {/* Drag-and-Drop Question List */}

        {/* Drag-and-Drop Question List */}
        {!isTemplateMode &&
          filteredQuestions &&
          filteredQuestions.length > 0 && (
            <div className="mt-3">
              {activeFilter === "active" ? (
                //  Active list — draggable
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="questions">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {filteredQuestions.map((q, index) => (
                          <Draggable
                            key={q.id ?? q.tempId}
                            draggableId={String(q.id ?? q.tempId)}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="d-flex justify-content-between align-items-center border rounded p-2 mb-2 bg-light"
                                style={{
                                  ...provided.draggableProps.style,
                                  userSelect: "none",
                                }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <strong>
                                    {index + 1}. {q.name}
                                  </strong>

                                  {/* Answer type */}
                                  <span
                                    className="badge"
                                    style={{
                                      backgroundColor:
                                        "rgba(0, 123, 255, 0.15)",
                                      color: "#0d6efd",
                                      borderRadius: "12px",
                                      fontWeight: "600",
                                      padding: "5px 10px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    {q.answerType}
                                  </span>

                                  {/* Required badge */}
                                  {q.required && (
                                    <span className="badge badge-required">
                                      Required
                                    </span>
                                  )}

                                  {/* Active badge */}
                                  <span
                                    className="badge"
                                    style={{
                                      backgroundColor:
                                        "rgba(40, 167, 69, 0.15)",
                                      color: "#28a745",
                                      borderRadius: "12px",
                                      fontWeight: "600",
                                      padding: "5px 10px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    Active
                                  </span>
                                </div>

                                {/* Edit + Delete icons */}
                                <div
                                  className="d-flex align-items-center"
                                  style={{ gap: "10px" }}
                                >
                                  <i
                                    className="fas fa-edit"
                                    style={{
                                      color: "#0a7674",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      handleEditQuestion(getQuestionIndex(q))
                                    }
                                    aria-hidden="true"
                                  ></i>

                                  <i
                                    className="fas fa-trash"
                                    style={{
                                      color: "#d62728",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      handleDeleteQuestion(getQuestionIndex(q))
                                    }
                                    title="Delete Question"
                                    aria-hidden="true"
                                  ></i>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                //  Inactive or All — not draggable
                <div>
                  {filteredQuestions.map((q, index) => (
                    <div
                      key={index}
                      className="d-flex justify-content-between align-items-center border rounded p-2 mb-2 bg-light"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <strong>
                          {index + 1}. {q.name}
                        </strong>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: "rgba(0, 123, 255, 0.15)",
                            color: "#0d6efd",
                            borderRadius: "12px",
                            fontWeight: "600",
                            padding: "5px 10px",
                            fontSize: "13px",
                          }}
                        >
                          {q.answerType}
                        </span>
                        {q.required && (
                          <span className="badge badge-required">
                              Required
                            </span>
                        )}

                        {/* Active / Inactive badge */}
                        <span
                          className="badge"
                          style={{
                            backgroundColor: q.is_active
                              ? "rgba(40, 167, 69, 0.15)"
                              : "rgba(220, 53, 69, 0.15)",
                            color: q.is_active ? "#28a745" : "#dc3545",
                            borderRadius: "12px",
                            fontWeight: "600",
                            padding: "5px 10px",
                            fontSize: "13px",
                          }}
                        >
                          {q.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>

                      {/* Edit + Delete icons */}
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: "10px" }}
                      >
                        <i
                          className="fas fa-edit"
                          style={{
                            color: "#0a7674",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            handleEditQuestion(getQuestionIndex(q))
                          }
                          aria-hidden="true"
                        ></i>

                        <i
                          className="fas fa-trash"
                          style={{
                            color: q.is_active ? "#d62728" : "#ccc",
                            cursor: q.is_active ? "pointer" : "not-allowed",
                            opacity: q.is_active ? 1 : 0.6,
                          }}
                          onClick={() => {
                            if (q.is_active) {
                              handleDeleteQuestion(getQuestionIndex(q));
                            }
                          }}
                          title={
                            q.is_active
                              ? "Delete Question"
                              : "Delete disabled for inactive questions"
                          }
                          aria-hidden="true"
                        ></i>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
      </Card.Body>

      {/*  Add/Edit Modal */}
      <Modal
        show={showQuestionModal}
        onHide={() => {
          setShowQuestionModal(false);
          setEditIndex(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editIndex !== null ? "Edit Question" : "Add Question"}
          </Modal.Title>
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={
            editIndex !== null
              ? values.questions[editIndex]
              : initialQuestionValues
          }
          validationSchema={QuestionSchema}
          //onSubmit={handleSaveQuestion}
          onSubmit={(formValues, formikHelpers) => {
            handleSaveQuestion(formValues, formikHelpers, submitType);
          }}
        >
          {({ values: questionValues, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Body>
                {/*-------Question--------*/}
                <div className="mb-3">
                  <label>
                    Question <span className="text-danger">*</span>
                  </label>
                  <Field
                    name="name"
                    className="form-control"
                    placeholder="Enter your question"
                    disabled={isEditMode && editIndex !== null}
                  />
                  <div className="text-danger">
                    <ErrorMessage name="name" />
                  </div>
                </div>
                {/*-------Answer Type--------*/}
                <div className="mb-3">
                  <label>
                    Answer Type <span className="text-danger">*</span>
                  </label>
                  <Field
                    as="select"
                    name="answerType"
                    className="form-select"
                    disabled={isEditMode && editIndex !== null}
                  >
                    <option value="text">Text (single line)</option>
                    <option value="textarea">Text Area (multi-line)</option>
                    <option value="number">Number</option>
                    <option value="date">Date picker</option>
                    <option value="dropdown">Dropdown (single select)</option>
                    <option value="radio">Radio (single choice)</option>
                    <option value="checkbox">Checkbox (multi-select)</option>
                    <option value="email">Email</option>
                    <option value="file">File upload </option>
                    <option value="url">URL / link </option>
                    <option value="info">Info</option>
                  </Field>
                  <div className="text-danger">
                    <ErrorMessage name="answerType" />
                  </div>
                </div>
                {/*-------Answer Validation--------*/}
                <div className="mb-3">
                  <label>Answer Validation</label>
                  <Field
                    name="answerValidation"
                    type="text"
                    className="form-control"
                    placeholder="Enter validation info (optional)"
                  />
                  <div className="text-danger">
                    <ErrorMessage name="answerValidation" />
                  </div>
                </div>
                {/*-------Required--------*/}
                <div className="mb-3 d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-4">
                    {/* Required checkbox */}
                    <div className="d-flex align-items-center">
                      <Field
                        type="checkbox"
                        name="required"
                        className="form-check-input me-2"
                      />
                      <label className="mb-0 me-3">Required</label>
                    </div>

                    {/* Active checkbox */}
                    <div className="d-flex align-items-center">
                      <Field
                        type="checkbox"
                        name="is_active"
                        className="form-check-input me-2"
                        checked={questionValues.is_active}
                        onChange={(e) => {
                          const newActive = e.target.checked;

                          // update form state
                          const updatedQuestionValues = {
                                ...questionValues,
                                is_active: newActive,
                          }; 

                          // immediately mark question active in main list
                          if (editIndex !== null) {
                            const updatedQuestions = [...values.questions];
                            const updatedQuestion = {
                              ...updatedQuestions[editIndex],
                              is_active: newActive,
                              order: newActive
                                ? updatedQuestions.filter((q) => q.is_active)
                                    .length + 1
                                : null,
                            };

                            // update order for all active questions again
                            let activeOrder = 1;
                            const reOrdered = updatedQuestions.map((q, i) => {
                              if (i === editIndex) return updatedQuestion;
                              if (q.is_active) {
                                return { ...q, order: activeOrder++ };
                              }
                              return { ...q, order: null };
                            });

                            setFieldValue("questions", reOrdered);
                          }
                        }}
                      />
                      <label className="mb-0 me-3">Active</label>
                    </div>
                  </div>

                  {/*----------Order--------*/}
                  <div className="d-flex align-items-center">
                    <label className="me-2 mb-0">Order</label>
                    <input
                      type="text"
                      className="form-control"
                      style={{ width: "80px" }}
                      value={questionValues.order ?? ""}
                      readOnly
                    />
                  </div>
                </div>
              </Modal.Body>

              <Modal.Footer>
                {/* <DefaultButton
                    buttonClass={"btn btn-secondary me-2"}
                    buttonTitle={"Cancel"} 
                    buttonType={"reset"}                    
                    actionBlock={() => {
                      setShowQuestionModal(false);
                      setEditIndex(null);                    
                     }} 
                /> */}
                <DefaultButton
                  buttonClass="btn btn-secondary default-btn-color"
                  buttonTitle="Save"
                  buttonType="button"
                  actionBlock={() => {
                    setSubmitType("save");
                    handleSubmit(); // no arguments
                  }}
                />

                <DefaultButton
                  buttonClass="btn btn-secondary default-btn-color"
                  buttonTitle="Save & New"
                  buttonType="button"
                  actionBlock={() => {
                    setSubmitType("save_new");
                    handleSubmit(); // no arguments
                  }}
                />
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </Card>
  );
};

export default CategoryQuestions;
